/**
 * The global keyboard model.
 *
 * Advanced users should never have to reach for the mouse, and the shortcuts must be the same
 * everywhere in the product. This module owns the definitions; docs/tangentbord.md documents
 * them and the in-app help (Skift + ?) renders them from this same list.
 */
import { useEffect } from "react";

export type Shortcut = {
  keys: string;
  description: string;
};

export const SHORTCUTS: Shortcut[] = [
  { keys: "Ctrl + K", description: "Öppna kommandopaletten" },
  { keys: "C", description: "Snabbfångst — fånga en tanke till Strömmen" },
  { keys: "Ctrl + Enter", description: "Spara fångsten i snabbfångstfältet" },
  { keys: "↑ ↓", description: "Flytta mellan träffar i paletten" },
  { keys: "Enter", description: "Kör markerad träff" },
  { keys: "Esc", description: "Stäng paletten, dialogen eller panelen" },
  { keys: "Skift + ?", description: "Visa den här listan" },
  { keys: "Ctrl + B / I / U", description: "Fet, kursiv, understruken i editorn" },
  { keys: "Ctrl + Alt + 1–3", description: "Rubriknivå i editorn" },
  { keys: "Tab", description: "Nästa cell i en tabell (Skift + Tab: föregående)" },
  { keys: "Ctrl + S", description: "Spara sidan direkt (autosparas annars)" },
  { keys: "J / K", description: "Nästa och föregående förslag i genomgången" },
  { keys: "A", description: "Acceptera förslaget i genomgången" },
  { keys: "E", description: "Ändra förslaget innan du accepterar" },
  { keys: "R", description: "Avvisa förslaget i genomgången" },
];

/** True when the keystroke belongs to whatever the user is typing in. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return (
    target.isContentEditable ||
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.tagName === "SELECT"
  );
}

type HotkeyOptions = {
  /** Ctrl on Windows and Linux, Cmd on macOS. */
  withModifier?: boolean;
  /** Fire even while the user is typing. Only for modifier combinations. */
  whileTyping?: boolean;
};

export function useHotkey(
  key: string,
  handler: () => void,
  { withModifier = false, whileTyping = false }: HotkeyOptions = {},
): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== key.toLowerCase()) {
        return;
      }
      if (withModifier !== (event.ctrlKey || event.metaKey)) {
        return;
      }
      if (!whileTyping && isTypingTarget(event.target)) {
        return;
      }

      event.preventDefault();
      handler();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [key, handler, withModifier, whileTyping]);
}
